import { cn } from '@/lib/utils';
import type { JobStatus } from '@/types/db';

/**
 * `<JobStatusBadge>` — UI-SPEC § 3.1 status pill.
 *
 * Reused 3 places: jobs list cards, job detail header, agent rows.
 * The four variants compose Phase 05 color tokens directly — no new
 * design tokens introduced this phase.
 *
 * Variants (status):
 * - `pending`   muted foam (no animation)
 * - `running`   tide-400 with `animate-pulse` (motion-reduce → solid fill)
 * - `completed` moss-500
 * - `failed`    coral-500
 *
 * Sizes:
 * - `sm` (default)  h-6 px-2 text-xs — fits in card rows
 * - `md`            h-8 px-3 text-sm — header band variant
 *
 * Accessibility:
 * - `role="status"` + `aria-label="Status: <Label>"` so screen readers
 *   announce state changes when the consumer re-renders the badge.
 * - Reduced-motion: the running variant swaps `animate-pulse` for a
 *   slightly stronger solid fill so the visual still differentiates.
 */
export interface JobStatusBadgeProps {
  status: JobStatus;
  size?: 'sm' | 'md';
  className?: string;
}

const STATUS_LABEL: Record<JobStatus, string> = {
  pending: 'Pending',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
};

const SIZE_CLASS: Record<'sm' | 'md', string> = {
  sm: 'h-6 px-2 text-xs gap-1.5',
  md: 'h-8 px-3 text-sm gap-2',
};

const STATUS_CLASS: Record<JobStatus, string> = {
  pending: 'bg-foam-50/10 text-foam-50/70 border-foam-50/20',
  running:
    'bg-tide-400/20 text-tide-400 border-tide-400/40 animate-pulse motion-reduce:animate-none motion-reduce:bg-tide-400/30',
  completed: 'bg-moss-500/20 text-moss-500 border-moss-500/40',
  failed: 'bg-coral-500/20 text-coral-100 border-coral-500/40',
};

export function JobStatusBadge({
  status,
  size = 'sm',
  className,
}: JobStatusBadgeProps) {
  const label = STATUS_LABEL[status];
  return (
    <span
      role="status"
      aria-label={`Status: ${label}`}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold border whitespace-nowrap',
        SIZE_CLASS[size],
        STATUS_CLASS[status],
        className,
      )}
    >
      {label}
    </span>
  );
}
